import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { VStack, Text, Box, Flex, Button, Icon, HStack } from '@chakra-ui/react';
import { FaChevronRight, FaInstagram, FaTiktok, FaTruck, FaStore, FaMapMarkerAlt, FaCalendarAlt, FaLaptop, FaShoppingCart, FaCommentDots } from 'react-icons/fa';
import { FiUsers, FiClock, FiUserPlus, FiExternalLink, FiFile, FiMapPin, FiCalendar, FiLock } from 'react-icons/fi';
import { Collab, Campaign, Client, SocialChannel, InfluencerLocation, DeliverableLink, activeUnits, isVoided } from '@foodfluence/shared';
import {
  getBrandManagedLogisticsBrandInstructions,
  getBrandManagedLogisticsDescription,
  getBrandManagedLogisticsLabel,
  BRAND_MANAGED_LOGISTICS_CONFIG,
  hasCampaignCompensationOffer,
} from '../../../../utils/brandManagedCampaignUtils';
import { formatFirebaseDate } from '../../../../utils/dateUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import LocationList from 'components/LocationList';
import InfluencerLocationPicker from 'components/InfluencerLocationPicker';
import { isUnconfirmedCollab, isUnconfirmedCollabOrPendingAcceptance } from '../../utils/collabUtils';
import { getEffectiveEndDate, isPreAcceptance } from 'utils/collabUtils';
import { getClosestLocations, getInfluencerPrimaryLocation } from 'utils/locationUtils';
import ShopifyProductDisplay from '../shopify/ShopifyProductDisplay';
import { useCampaignLocations } from '../../../../hooks/useCampaignLocations';
import { DEFAULT_MAX_PARTY_SIZE } from '../../scheduling/utils/partySizeUtils'
import CollabInstructions from './CollabInstructions';
import BrandHeader from './BrandHeader';
import CollabCompensation from './CollabCompensation';
import { getChannelIcon } from '../../../../utils/channelUtils';
import UgcCreativeBrief from './UgcCreativeBrief';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../../firebase';
import FoodFluenceCard from '../../../../components/FoodFluenceCard';
import MarkdownText from 'components/MarkdownText';
import { goLivePostText } from '../../utils/postByLabels';
import { resolveDueDateTimezone } from '../../../../utils/timezoneUtils';
import { InfluencerDataContext } from '../../../../contexts/InfluencerDataContext';
import { getCollabContentRequirements } from '../../../../utils/contentUtils';
import { shouldShowVerificationOverlay } from '../../../../utils/authUtils';
import PhoneVerificationModal from '../../../../components/PhoneVerificationModal';
import { getBriefGoLive, getTurnaroundText, getTurnaroundTitle } from '../../utils/creativeBriefUtils';
import { collabApi } from '../../../../services/api/collab';
import { useInfluencerLocations } from '../../../../contexts/InfluencerLocationContext';
import { ff } from '../../../../theme';
import { Pill } from '../../../../components/ui';

interface CreativeBriefProps {
  collab: Collab;
  campaign: Campaign;
  isRequest?: boolean;
  client?: Client | null;
  actionsSection?: React.ReactNode;
  hideBrandHeader?: boolean;
  selectedLocationIds?: string[];
  onLocationClick?: (docId: string) => void;
}

const CARD = { border: '1px solid', borderColor: ff.card, borderRadius: '20px' } as const;
const ROW_DIVIDER = <Box h="1px" bg={ff.line} border="none" />;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="h2" m={0} mb={3} textStyle="eyebrow" fontSize="12px" color={ff.text2}>
    {children}
  </Text>
);

/** Fact row: icon + label on the left, value on the right. */
const FactRow: React.FC<{ icon: React.ElementType; label: React.ReactNode; children: React.ReactNode }> = ({ icon, label, children }) => (
  <HStack minH="52px" py={3} spacing={3} align="center" justify="space-between" fontSize="15px">
    <HStack spacing="10px" flex={1} minW={0} color={ff.text2}>
      <Icon as={icon} boxSize="18px" flex="none" aria-hidden />
      <Text as="span">{label}</Text>
    </HStack>
    <Text as="span" fontWeight={600} color={ff.ink} textAlign="right">
      {children}
    </Text>
  </HStack>
);

/** Frosted lock over gated content; tapping Verify opens phone verification. */
const VerifyOverlay: React.FC<{ onVerify: () => void }> = ({ onVerify }) => (
  <Flex
    position="absolute"
    inset={0}
    bg="rgba(255, 255, 255, 0.85)"
    backdropFilter="blur(4px)"
    borderRadius="20px"
    align="center"
    justify="center"
    zIndex={1}
    overflow="hidden"
    fontFamily="display"
  >
    <VStack spacing={3} align="center" px={6}>
      <Flex boxSize="56px" bg={ff.ink} borderRadius="full" align="center" justify="center">
        <Icon as={FiLock} color={ff.lime} boxSize="24px" />
      </Flex>
      <Text textStyle="display" fontSize="24px" color={ff.ink} textAlign="center">
        Verify to view full offer
      </Text>
      <Text fontSize="15px" color={ff.text2} textAlign="center" lineHeight="1.5">
        Quick verification to ensure this offer reaches you
      </Text>
      <Button variant="ink" h="48px" px={6} mt={1} onClick={onVerify}>
        Verify now
      </Button>
    </VStack>
  </Flex>
);

const BRAND_MANAGED_LOGISTICS_ICONS: Record<keyof typeof BRAND_MANAGED_LOGISTICS_CONFIG, React.ElementType> = {
  ship: FaTruck,
  retailPickup: FaStore,
  creatorVisit: FaMapMarkerAlt,
  serviceDate: FaCalendarAlt,
  digital: FaLaptop,
  onlinePurchase: FaShoppingCart,
  other: FaCommentDots,
};

const CHANNEL_SHORT_NAMES: Record<string, string> = {
  tiktoks: 'TikTok', instagramReels: 'Reel',
  instagramStories: 'Story', instagramPosts: 'Post',
};


const DeliverableCard: React.FC<{
  channel: SocialChannel;
  count: number;
  onClick?: () => void;
}> = ({ channel, count, onClick }) => {
  const Icon_ = channel === 'tiktoks' ? FaTiktok : FaInstagram;
  return (
    <HStack
      minH="56px"
      py={3}
      spacing={3}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
    >
      <Flex boxSize="36px" flex="none" borderRadius="full" bg={ff.grey} color={ff.ink} align="center" justify="center">
        <Icon as={Icon_} boxSize="18px" />
      </Flex>
      <Text flex={1} minW={0} fontSize="15px" fontWeight={600} color={ff.ink}>
        {CHANNEL_SHORT_NAMES[channel] || 'Content'}
      </Text>
      <Pill tone="grey">{`${count}× required`}</Pill>
    </HStack>
  );
};

const CreativeBrief: React.FC<CreativeBriefProps> = ({
  collab,
  campaign,
  isRequest,
  client,
  actionsSection,
  hideBrandHeader,
  selectedLocationIds,
  onLocationClick,
}) => {
  const clients = useContext(ClientDataContext);
  const [user] = useAuthState(auth);
  const { influencerData } = useContext(InfluencerDataContext);
  const turnaroundText = getTurnaroundText(campaign, collab, resolveDueDateTimezone(influencerData?.timezone));
  const { influencerLocations } = useInfluencerLocations();
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [spotsRemaining, setSpotsRemaining] = useState<number | null>(null);
  const [selectedInfluencerLocation, setSelectedInfluencerLocation] = useState<InfluencerLocation | null>(null);

  /**
   * What the creator still owes, per platform: `[platform, activeCount]` for the deliverables strip.
   *
   * Counted from `deliverableLinks` because a voided slot keeps its array position and is identified
   * by `status: 'voided'`, so the live total is the non-voided length. Falls back to
   * `deliverableCounts` for a platform with no slots yet — links are seeded at acceptance, and a
   * brief read before that would otherwise show an empty strip.
   */
  const activeDeliverableCounts = useMemo<Array<[string, number]>>(() => {
    const links = (collab?.deliverableLinks ?? {}) as Record<string, DeliverableLink[]>;
    const counts = (collab?.deliverableCounts ?? {}) as Record<string, number>;
    const platforms = Array.from(new Set([...Object.keys(counts), ...Object.keys(links)]));

    return platforms
      .map((platform): [string, number] => {
        const slots = links[platform];
        const active = Array.isArray(slots) && slots.length > 0
          ? slots.filter((slot) => !isVoided(slot)).length
          : (counts[platform] ?? 0);
        return [platform, active];
      })
      .filter(([, active]) => active > 0);
  }, [collab?.deliverableLinks, collab?.deliverableCounts]);

  const isUnconfirmedCollaboration = isUnconfirmedCollab(collab, campaign);
  const isUnconfirmedCollaborationOrPendingAcceptance = isUnconfirmedCollabOrPendingAcceptance(collab, campaign);

  // Auto-select primary (priority 1), or first location in list if none marked primary
  useEffect(() => {
    if (selectedInfluencerLocation || influencerLocations.length === 0) return;
    setSelectedInfluencerLocation(getInfluencerPrimaryLocation(influencerLocations));
  }, [influencerLocations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      isUnconfirmedCollaboration &&
      campaign?.maxCollabs != null &&
      collab.influencerCampaignID &&
      (campaign?.campaignType === 'inStore' || campaign?.campaignType === 'retail')
    ) {
      collabApi.getAvailableSpots(collab.influencerCampaignID, collab.id)
        .then((res) => setSpotsRemaining(res.availableSpots))
        .catch((err) => console.error('Error fetching available spots:', err));
    }
  }, [isUnconfirmedCollaboration, campaign?.maxCollabs, campaign?.campaignType, collab.influencerCampaignID, collab.id]);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    const check = () => setDescriptionOverflows(el.scrollHeight > el.clientHeight);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [campaign.description]);

  if (!client) {
    client = clients[campaign.clientID];
  }
  
  // Fetch locations for the campaign
  const { locations, loading: locationsLoading, error: locationsError } = useCampaignLocations(
    campaign.foodfluenceLocations
  );

  // Show only 3 closest locations for unconfirmed collabs (review and request flows)
  const displayLocations = !isUnconfirmedCollaborationOrPendingAcceptance
    ? locations
    : getClosestLocations(
        locations,
        selectedInfluencerLocation
          ? { lat: selectedInfluencerLocation.lat, lng: selectedInfluencerLocation.lng }
          : null,
        3
      );

  const resolvedContentRequirements = getCollabContentRequirements(collab, campaign);
  const hasResolvedUgcRequirements =
    !!resolvedContentRequirements && Object.keys(resolvedContentRequirements).length > 0;

  return (
    <VStack spacing={8} align="stretch" fontFamily="display" color={ff.ink}>

      {/* Header Section */}
      {!hideBrandHeader && <BrandHeader client={client} campaign={campaign} />}

      {actionsSection}

      {/* FoodFluence Card for users without phone number */}
      {shouldShowVerificationOverlay(user, influencerData) && (
        <FoodFluenceCard />
      )}

      {/* Campaign Section */}
      <Box>
        <SectionTitle>Campaign</SectionTitle>
        <Box {...CARD} p="18px">
          <Text fontSize="20px" fontWeight={600} lineHeight="1.25" mb={2}>
            {campaign?.name ?? ''}
          </Text>
          <MarkdownText
            ref={descriptionRef}
            fontSize="15px"
            color={ff.body}
            lineHeight="1.5"
            // Three lines at 1.5 line-height; noOfLines can't clamp Markdown's block children.
            maxH={isDescriptionExpanded ? undefined : "4.5em"}
            overflow="hidden"
          >
            {campaign.description}
          </MarkdownText>
          {(descriptionOverflows || isDescriptionExpanded) && (
            <Flex justify="flex-end" mt={3}>
              <Button
                variant="inkOutline"
                h="44px"
                px={4}
                fontSize="14px"
                rightIcon={<Icon as={FaChevronRight} boxSize={2.5} />}
                onClick={() => setIsDescriptionExpanded(prev => !prev)}
              >
                {isDescriptionExpanded ? 'Close brief' : 'Open brief'}
              </Button>
            </Flex>
          )}
        </Box>
      </Box>

      {/* Logistics Section (brand-managed campaigns only) */}
      {campaign?.brandManagedCollabLogistics?.type && (() => {
        const logistics = campaign.brandManagedCollabLogistics!;
        const logisticsType = logistics.type;
        const description = getBrandManagedLogisticsDescription(logistics);
        const brandInstructions = getBrandManagedLogisticsBrandInstructions(logistics);
        const label = getBrandManagedLogisticsLabel(logistics);
        const icon = BRAND_MANAGED_LOGISTICS_ICONS[logisticsType];
        if (!description && !brandInstructions) return null;
        return (
          <Box>
            <SectionTitle>Logistics</SectionTitle>
            <Box {...CARD} p="18px">
              <VStack align="start" spacing={3}>
                <HStack spacing={3} align="center">
                  <Flex boxSize="36px" flex="none" borderRadius="full" bg={ff.grey} color={ff.ink} align="center" justify="center">
                    <Icon as={icon} boxSize={4} />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600}>
                    {label}
                  </Text>
                </HStack>
                {description ? (
                  <Text fontSize="15px" color={ff.body} lineHeight="1.5">
                    {description}
                  </Text>
                ) : null}
                {brandInstructions ? (
                  <Box w="100%" pt={3} borderTop="1px solid" borderColor={ff.line}>
                    <Text fontSize="14px" fontWeight={600} mb={1}>
                      Instructions from brand:
                    </Text>
                    <Text fontSize="15px" color={ff.body} lineHeight="1.5">
                      {brandInstructions}
                    </Text>
                  </Box>
                ) : null}
              </VStack>
            </Box>
          </Box>
        );
      })()}

      {/* Locations (brand-managed: display-only, the visit itself is coordinated with the brand) */}
      {campaign?.campaignType === "brandManaged" &&
       (campaign.foodfluenceLocations?.length ?? 0) > 0 && (
        <LocationList
          locations={locations || []}
          title="Locations"
          isLoading={locationsLoading}
          error={locationsError}
        />
      )}

      {/* General areas (brand-managed general-locations mode) */}
      {campaign?.campaignType === "brandManaged" &&
       (campaign.foodfluenceLocations?.length ?? 0) === 0 &&
       Object.keys(campaign?.allowedLocations ?? {}).length > 0 && (
        <Box>
          <SectionTitle>Regions</SectionTitle>
          <Box {...CARD} px={4}>
            <VStack align="stretch" spacing={0} divider={ROW_DIVIDER}>
              {Object.entries(campaign.allowedLocations!).map(([country, states]) => (
                <Text key={country} py={3} fontSize="15px" color={ff.body}>
                  {states.includes('__WHOLE_COUNTRY__') ? country : `${states.join(', ')} (${country})`}
                </Text>
              ))}
            </VStack>
          </Box>
        </Box>
      )}

      {/* Available Locations Section */}
      {(campaign?.campaignType === "inStore" || campaign?.campaignType === "retail") &&
       (locations || locationsLoading || locationsError) && (
        <Box
          position="relative"
          minH={shouldShowVerificationOverlay(user, influencerData) ? "300px" : "auto"}
        >
          <VStack align="stretch" spacing={3}>
            {isUnconfirmedCollaborationOrPendingAcceptance && !selectedInfluencerLocation ? (
              <>
                <Text as="h2" m={0} textStyle="eyebrow" fontSize="12px" color={ff.text2}>
                  {isRequest ? "Select from available locations" : "Available locations"}
                </Text>
                <InfluencerLocationPicker
                  onConfirm={(loc) => setSelectedInfluencerLocation(loc)}
                />
              </>
            ) : (
              <>
                <LocationList
                  locations={
                    isUnconfirmedCollaborationOrPendingAcceptance
                      ? displayLocations
                      : collab?.selectedLocation && locations
                        ? locations.filter((loc) => loc.docId === collab.selectedLocation)
                        : []
                  }
                  title={
                    isUnconfirmedCollaborationOrPendingAcceptance
                      ? (isRequest ? "Select from Available Locations" : "Available Locations")
                      : "Selected Location"
                  }
                  isLoading={locationsLoading}
                  error={locationsError}
                  isSelectable={isRequest && isUnconfirmedCollaborationOrPendingAcceptance}
                  selectedLocationIds={selectedLocationIds}
                  onLocationClick={onLocationClick}
                />
                {isUnconfirmedCollaborationOrPendingAcceptance && selectedInfluencerLocation && displayLocations.length > 0 && (
                  <HStack
                    as="button"
                    alignSelf="flex-start"
                    minH="44px"
                    spacing={1.5}
                    cursor="pointer"
                    onClick={() => setSelectedInfluencerLocation(null)}
                    _hover={{ opacity: 0.75 }}
                    transition="opacity 0.15s"
                  >
                    <Icon as={FiMapPin} color={ff.text2} boxSize="14px" flexShrink={0} />
                    <Text fontSize="13px" color={ff.text2}>
                      Nearest spots to {selectedInfluencerLocation.zipCode}
                      {' · '}
                      <Text as="span" color={ff.ink} fontWeight={600} textDecoration="underline">Change</Text>
                    </Text>
                  </HStack>
                )}
              </>
            )}
          </VStack>

          {/* Verification overlay */}
          {shouldShowVerificationOverlay(user, influencerData) && (
            <VerifyOverlay onVerify={() => setIsPhoneModalOpen(true)} />
          )}
        </Box>
      )}

      {/* Content Deliverables Section */}
      {(!isRequest || hasResolvedUgcRequirements) && (
        <Box>
          {hasResolvedUgcRequirements ? (
            <UgcCreativeBrief campaign={campaign} collab={collab} clientName={client?.accountName} />
          ) : (
            <>
              <SectionTitle>Deliverables</SectionTitle>
              {/*
                One row per platform, counted from the slots that are still owed rather than from
                `deliverableCounts`. The count field records what the brand originally asked for and
                is not decremented when a slot is voided, so reading it here showed the pre-void
                total — and the strip used to carry a second greyed card to compensate, which
                double-counted the removal when the count HAD been decremented.
                Counting active slots is correct either way, and the creator learns about the change
                from the `deliverablesUpdated` note in their thread.
              */}
              {activeDeliverableCounts.length > 0 && (
                <Box {...CARD} px={4}>
                  <VStack align="stretch" spacing={0} divider={ROW_DIVIDER}>
                    {activeDeliverableCounts.map(([key, active]) => (
                      <DeliverableCard
                        key={key}
                        channel={key as SocialChannel}
                        count={active}
                      />
                    ))}
                  </VStack>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Additional Info Section */}
      {(campaign?.additionalInfoFile || campaign?.additionalInfoLink) && (
        <Box>
          <SectionTitle>Additional info</SectionTitle>
          <Box {...CARD} px={4}>
            <VStack spacing={0} align="stretch" divider={ROW_DIVIDER}>
              {campaign.additionalInfoFile && (
                <HStack minH="60px" py={2} spacing={3} align="center" justify="space-between">
                  <HStack spacing="10px" flex={1} minW={0}>
                    <Icon as={FiFile} boxSize="18px" color={ff.text2} flex="none" />
                    <Text fontSize="15px" fontWeight={600}>
                      Brief document
                    </Text>
                  </HStack>
                  <Button as="a" href={campaign.additionalInfoFile} target="_blank" rel="noopener noreferrer" variant="inkOutline" h="44px" px={4} fontSize="14px" rightIcon={<Icon as={FiExternalLink} boxSize={3} />}>
                    Open
                  </Button>
                </HStack>
              )}
              {campaign.additionalInfoLink && (
                <HStack minH="60px" py={2} spacing={3} align="center" justify="space-between">
                  <HStack spacing="10px" flex={1} minW={0}>
                    <Icon as={FiExternalLink} boxSize="18px" color={ff.text2} flex="none" />
                    <Text fontSize="15px" fontWeight={600}>
                      Additional link
                    </Text>
                  </HStack>
                  <Button as="a" href={campaign.additionalInfoLink} target="_blank" rel="noopener noreferrer" variant="inkOutline" h="44px" px={4} fontSize="14px" rightIcon={<Icon as={FiExternalLink} boxSize={3} />}>
                    Open
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>
        </Box>
      )}

      {/* Details Section */}
      {((campaign?.maxPartySize && campaign?.campaignType === 'inStore') ||
        turnaroundText ||
        getBriefGoLive(campaign, collab) ||
        getEffectiveEndDate(campaign, collab) ||
        (isUnconfirmedCollaboration && !isRequest && spotsRemaining != null && (campaign?.campaignType === 'inStore' || campaign?.campaignType === 'retail'))) && (
        <Box>
          <SectionTitle>Details</SectionTitle>
          <Box {...CARD} px={4}>
            <VStack spacing={0} align="stretch" divider={ROW_DIVIDER}>
            {campaign?.maxPartySize && campaign?.campaignType === 'inStore' && (
              <FactRow icon={FiUsers} label="Party size">
                Up to {campaign.maxPartySize ?? DEFAULT_MAX_PARTY_SIZE}
              </FactRow>
            )}
            {turnaroundText && (
              <FactRow icon={FiClock} label={getTurnaroundTitle(campaign, collab)}>
                {turnaroundText}
              </FactRow>
            )}
            {(() => {
              const goLive = getBriefGoLive(campaign, collab);
              return goLive && (
                <FactRow icon={FiCalendar} label="Go-live date">
                  {goLivePostText(goLive)}
                </FactRow>
              );
            })()}
            {isUnconfirmedCollaboration && !isRequest && spotsRemaining != null && (campaign?.campaignType === 'inStore' || campaign?.campaignType === 'retail') && (
              <FactRow icon={FiUserPlus} label="Spots remaining">
                {spotsRemaining}
              </FactRow>
            )}
            </VStack>
          </Box>
        </Box>
      )}

      {/* Tagging Instructions Section */}
      {!isRequest && (client?.instagramHandle || client?.tiktokHandle) && (
        // Check if there are any tagging instructions to show
        (client?.instagramHandle && (
          (collab?.content && activeUnits(collab.content).some(unit =>
            unit.channels?.some(channel =>
              channel === 'instagramPosts' || channel === 'instagramReels' || channel === 'instagramStories'
            )
          )) || 
          Object.entries(collab?.deliverableCounts ?? {})
            .some(([key, count]) => key.includes('instagram') && (count ?? 0) > 0)
        )) ||
        (client?.tiktokHandle && (
          (collab?.content && activeUnits(collab.content).some(unit =>
            unit.channels?.some(channel => channel === 'tiktoks')
          )) || 
          Object.entries(collab?.deliverableCounts ?? {})
            .some(([key, count]) => key.includes('tiktoks') && (count ?? 0) > 0)
        ))
      ) && (
        <Box>
          <SectionTitle>Tagging instructions</SectionTitle>
          <Box {...CARD} px={4}>
            <VStack spacing={0} align="stretch" divider={ROW_DIVIDER}>
              {client?.instagramHandle && (
                (collab?.content && activeUnits(collab.content).some(unit => 
                  unit.channels?.some(channel => 
                    channel === 'instagramPosts' || channel === 'instagramReels' || channel === 'instagramStories'
                  )
                )) || 
                Object.entries(collab?.deliverableCounts ?? {})
            .some(([key, count]) => key.includes('instagram') && (count ?? 0) > 0)
              ) && (
                <HStack py={3} spacing={3} align="center">
                  <Flex boxSize="36px" flex="none" borderRadius="full" bg={ff.grey} color={ff.ink} align="center" justify="center">
                    <Icon as={getChannelIcon('instagramPosts')} boxSize="18px" />
                  </Flex>
                  <Text fontSize="15px" color={ff.body} lineHeight="1.4">
                    Tag and add @{client.instagramHandle} on IG as collaborator
                  </Text>
                </HStack>
              )}
              {client?.tiktokHandle && (
                (collab?.content && activeUnits(collab.content).some(unit => 
                  unit.channels?.some(channel => channel === 'tiktoks')
                )) || 
                Object.entries(collab?.deliverableCounts ?? {})
            .some(([key, count]) => key.includes('tiktoks') && (count ?? 0) > 0)
              ) && (
                <HStack py={3} spacing={3} align="center">
                  <Flex boxSize="36px" flex="none" borderRadius="full" bg={ff.grey} color={ff.ink} align="center" justify="center">
                    <Icon as={getChannelIcon('tiktoks')} boxSize="18px" />
                  </Flex>
                  <Text fontSize="15px" color={ff.body} lineHeight="1.4">
                    Tag and add @{client.tiktokHandle} on TikTok as collaborator
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </Box>
      )}

      {/* Compensation Section */}
      {hasCampaignCompensationOffer(campaign, collab) && (
        <Box 
          position="relative"
          minH={shouldShowVerificationOverlay(user, influencerData) ? "300px" : "auto"}
        >
          <CollabCompensation collab={collab} campaign={campaign} isRequest={isRequest} instructionsOnly={isPreAcceptance(collab) && !isRequest} />
          
          {/* Verification overlay for users without phone number */}
          {shouldShowVerificationOverlay(user, influencerData) && (
            <VerifyOverlay onVerify={() => setIsPhoneModalOpen(true)} />
          )}
        </Box>
      )}

      {/* Content Guidelines Section */}
      <CollabInstructions 
        isRequest={isRequest} 
        campaign={campaign}
        collab={collab}
      />

      {/* Shopify Products Section */}
      {campaign?.campaignType === "shopify" && (
        <Box>
          <SectionTitle>Products</SectionTitle>
          <Box {...CARD} p={4}>
            <ShopifyProductDisplay
              clientId={campaign?.clientID}
              productVariants={campaign?.shopifyProducts || {}}
              orderId={collab?.shopifyOrderID || ""}
            />
          </Box>
        </Box>
      )}
      
      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
      />

    </VStack>
  );
};

export default CreativeBrief;